import { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { inventoryReturnPath } from './inventoryRouteState';
import { Terminal } from 'xterm';
import { FitAddon } from 'xterm-addon-fit';
import 'xterm/css/xterm.css';
import OrgModuleLayout from './OrgModuleLayout';
import OrgModulePlaceholder from './OrgModulePlaceholder';
import { TerminalIcon } from '@app/components/icons';
import { useActiveOrgId } from '@app/hooks/useActiveOrg';
import { useDevicesApi } from '@app/hooks/useApis';
import { useApiConfiguration } from '@app/hooks/useApiConfiguration';
import { ApiResponseError, buildOrgUrl, cloneHeadersWithAuth } from '@app/utils/api';

interface DeviceSshSessionResponse {
  sessionId?: string | null;
  wsUrl?: string | null;
  webUrl?: string | null;
  url?: string | null;
  sshUrl?: string | null;
}

type SessionStatus = 'idle' | 'loading' | 'connecting' | 'connected' | 'closed' | 'error';

const DeviceSshPage = () => {
  const { deviceId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const returnToInventory = inventoryReturnPath((location.state as { from?: unknown } | null)?.from);
  const orgId = useActiveOrgId();
  const devicesApi = useDevicesApi();
  const apiConfig = useApiConfiguration();
  const basePath = apiConfig.basePath ?? '';

  const terminalContainerRef = useRef<HTMLDivElement | null>(null);
  const terminalRef = useRef<Terminal | null>(null);
  const fitRef = useRef<FitAddon | null>(null);
  const socketRef = useRef<WebSocket | null>(null);
  const dataHandlerRef = useRef<{ dispose: () => void } | null>(null);
  const [terminalReady, setTerminalReady] = useState(false);

  const [status, setStatus] = useState<SessionStatus>('idle');
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [deviceName, setDeviceName] = useState<string | null>(null);
  const [deviceHostname, setDeviceHostname] = useState<string | null>(null);

  const deviceLabel = useMemo(() => deviceName ?? deviceHostname ?? deviceId ?? 'device', [deviceHostname, deviceId, deviceName]);
  const showError = useMemo(() => {
    if (!error) {
      return false;
    }
    const normalized = error.trim().toLowerCase();
    return normalized !== 'ssh connection failed.' && normalized !== 'connection failed.';
  }, [error]);
  const statusMark = useMemo(() => {
    if (status === 'connected') {
      return (
        <span className="ssh-terminal__status-mark ssh-terminal__status-mark--ok" aria-label="Connected" title="Connected">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M3.5 8.5l2.8 2.8 6.2-6.2" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      );
    }
    if (status === 'error') {
      return (
        <span className="ssh-terminal__status-mark ssh-terminal__status-mark--error" aria-label="Connection failed" title="Connection failed">
          <svg viewBox="0 0 16 16" aria-hidden="true">
            <path d="M4 4l8 8M12 4l-8 8" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </span>
      );
    }
    return (
      <span className="ssh-terminal__status-mark ssh-terminal__status-mark--pending" aria-label="Connecting" title="Connecting">
        <svg viewBox="0 0 16 16" aria-hidden="true">
          <circle cx="8" cy="8" r="5" fill="none" stroke="currentColor" strokeWidth="2" />
        </svg>
      </span>
    );
  }, [status]);

  useEffect(() => {
    if (!terminalContainerRef.current) {
      return;
    }
    const terminal = new Terminal({
      convertEol: false,
      cursorBlink: true,
      fontFamily: '"DM Mono", ui-monospace, "SFMono-Regular", Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
      fontSize: 13,
      theme: {
        background: '#0b0f14',
        foreground: '#e2e8f0'
      }
    });
    const fitAddon = new FitAddon();
    terminal.loadAddon(fitAddon);
    terminal.open(terminalContainerRef.current);
    fitAddon.fit();
    terminalRef.current = terminal;
    fitRef.current = fitAddon;
    setTerminalReady(true);

    const observer = new ResizeObserver(() => {
      fitAddon.fit();
    });
    observer.observe(terminalContainerRef.current);

    return () => {
      observer.disconnect();
      dataHandlerRef.current?.dispose();
      socketRef.current?.close();
      terminal.dispose();
      terminalRef.current = null;
      fitRef.current = null;
      setTerminalReady(false);
    };
  }, []);

  useEffect(() => {
    if (!orgId || !deviceId || !terminalReady) {
      return;
    }

    let cancelled = false;
    const openSession = async () => {
      setStatus('loading');
      setError(null);
      setSessionId(null);

      const resource = `devices/${encodeURIComponent(deviceId)}/ssh-sessions`;
      const url = buildOrgUrl(basePath, orgId, resource);
      const headers = await cloneHeadersWithAuth({
        'Content-Type': 'application/json',
        Accept: 'application/json'
      });
      const response = await fetch(url, {
        method: 'POST',
        headers,
        body: JSON.stringify({})
      });
      if (!response.ok) {
        throw new ApiResponseError(response, `Request failed with status ${response.status}`);
      }
      const payload = (await response.json()) as DeviceSshSessionResponse;
      // Debug: trace session creation + websocket init issues.
      console.debug('[ssh] session created', { deviceId, sessionId: payload.sessionId, wsUrl: payload.wsUrl, url: payload.url });
      if (cancelled) {
        return;
      }
      const wsUrl = payload.wsUrl ?? payload.url ?? payload.webUrl ?? payload.sshUrl ?? null;
      if (!wsUrl) {
        throw new Error('SSH session did not return a websocket URL.');
      }
      setSessionId(payload.sessionId ?? null);
      setStatus('connecting');

      console.debug('[ssh] opening websocket', { deviceId, sessionId: payload.sessionId, wsUrl });
      const socket = new WebSocket(wsUrl);
      socketRef.current = socket;
      socket.binaryType = 'arraybuffer';

      const terminal = terminalRef.current;
      if (!terminal) {
        throw new Error('Terminal is not ready.');
      }

      dataHandlerRef.current?.dispose();
      dataHandlerRef.current = terminal.onData((data: string) => {
        if (socket.readyState === WebSocket.OPEN) {
          socket.send(data);
        }
      });

      socket.onopen = () => {
        if (cancelled) {
          return;
        }
        console.debug('[ssh] websocket open', { deviceId, sessionId: payload.sessionId });
        terminal.focus();
      };

      socket.onmessage = (event) => {
        if (!terminal) {
          return;
        }
        setStatus((prev) => (prev === 'connected' ? prev : 'connected'));
        if (typeof event.data === 'string') {
          terminal.write(event.data);
          return;
        }
        terminal.write(new Uint8Array(event.data));
      };

      socket.onerror = () => {
        if (cancelled) {
          return;
        }
        console.debug('[ssh] websocket error', { deviceId, sessionId: payload.sessionId });
        setStatus('error');
        setError('SSH connection failed.');
      };

      socket.onclose = (event) => {
        if (cancelled) {
          return;
        }
        console.debug('[ssh] websocket close', { deviceId, sessionId: payload.sessionId });
        const reason = event.reason?.trim();
        const ignoredReasons = new Set(['session closed', 'client closed', 'stream closed']);
        if (reason && !ignoredReasons.has(reason.toLowerCase())) {
          setStatus('error');
          setError(reason);
          return;
        }
        setStatus((prev) => (prev === 'error' ? prev : 'closed'));
      };
    };

    openSession().catch((caught: unknown) => {
      if (cancelled) {
        return;
      }
      const message =
        caught instanceof ApiResponseError
          ? `Unable to create SSH session (${caught.response.status}).`
          : caught instanceof Error
          ? caught.message
          : 'Unable to create SSH session.';
      setStatus('error');
      setError(message);
    });

    return () => {
      cancelled = true;
      socketRef.current?.close();
      dataHandlerRef.current?.dispose();
    };
  }, [basePath, deviceId, orgId, terminalReady]);

  useEffect(() => {
    if (!orgId || !deviceId) {
      return;
    }
    let cancelled = false;
    const loadDevice = async () => {
      try {
        const payload = await devicesApi.orgsOrgIdDevicesDeviceIdGet({ orgId, deviceId });
        if (!cancelled) {
          setDeviceName(payload.displayName ?? null);
          setDeviceHostname(payload.hostname ?? null);
        }
      } catch {
        if (!cancelled) {
          setDeviceName(null);
          setDeviceHostname(null);
        }
      }
    };
    void loadDevice();
    return () => {
      cancelled = true;
    };
  }, [deviceId, devicesApi, orgId]);

  if (!orgId) {
    return (
      <OrgModuleLayout title="SSH" description="Connect to a managed device.">
        <OrgModulePlaceholder intent="Select an organization to start a session." />
      </OrgModuleLayout>
    );
  }

  if (!deviceId) {
    return (
      <OrgModuleLayout title="SSH" description="Connect to a managed device.">
        <OrgModulePlaceholder intent="Choose a device from Inventory to start a session." />
      </OrgModuleLayout>
    );
  }

  return (
    <OrgModuleLayout
      title="SSH"
      description={
        <span>
          Secure shell session to Device <strong>{deviceLabel}</strong> {statusMark}
        </span>
      }
      actions={
        <div className="button-row">
          <button type="button" className="ui-button ui-button--ghost" onClick={() => navigate(returnToInventory)}>
            Back to inventory
          </button>
        </div>
      }
    >
      <div className="ssh-terminal">
        {showError && <p className="form-error">{error}</p>}
        {sessionId && !error && null}
        <div className="ssh-terminal__panel">
          <div className="ssh-terminal__viewport" ref={terminalContainerRef} />
        </div>
      </div>
    </OrgModuleLayout>
  );
};

export default DeviceSshPage;
